import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { isCancel } from '@/utils/axios';
import { hospitalHeaders } from '@/utils';
import type {
    CensusReportDetailItem,
    CensusReportListItem,
    CensusReportsResponse,
    HospitalUser,
    HospitalUsersResponse,
    SaveCensusCoveragePatientsRequestItem,
    SaveCensusCoveragePatientsResponse,
    UsersSearchResponse
} from '@/types/census';
import { getErrorMessage } from '@/utils/error-message';

interface FetchCensusReportsParams {
    page?: number;
    limit?: number;
    search?: string;
    unit?: string;
    hospitalId?: string;
}

interface FetchCensusReportDetailsParams {
    unitRangeIds: string[];
    hospitalId?: string;
    coveragePatientIds?: string[];
}

interface SaveCensusCoveragePatientsParams {
    items: SaveCensusCoveragePatientsRequestItem[];
    hospitalId: string;
}

interface AssignCareNavigatorParams {
    userId: string;
    unitRangeId: string;
    hospitalId?: string;
}

interface FetchUsersForAssignmentParams {
    offset?: number;
    limit?: number;
    search?: string;
    hospitalId?: string;
    organizationKey?: string;
}
export const fetchCensusReports = createAsyncThunk<
    {
        items: CensusReportListItem[];
        total: number;
        page: number;
        limit: number;
    },
    FetchCensusReportsParams | undefined,
    { rejectValue: string }
>('census/fetchCensusReports', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<CensusReportsResponse>('patients/census-reports-ranges', {
            params: {
                page: params?.page ?? 1,
                limit: params?.limit ?? 50,
                search: params?.search || undefined,
                unit: params?.unit || undefined
            },
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        return {
            items: data.items ?? [],
            total: data.total ?? 0,
            page: data.page ?? params?.page ?? 1,
            limit: data.limit ?? params?.limit ?? 50
        };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch census reports'));
    }
});

export const fetchHospitalUsers = createAsyncThunk<HospitalUser[], void, { rejectValue: string }>(
    'census/fetchHospitalUsers',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<HospitalUsersResponse>('hospitals/users');
            return data.items;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch hospital users'));
        }
    }
);

export const fetchUsersForAssignment = createAsyncThunk<HospitalUser[], FetchUsersForAssignmentParams | undefined, { rejectValue: string }>(
    'census/fetchUsersForAssignment',
    async (params, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.get<UsersSearchResponse>('users', {
                params: {
                    offset: params?.offset ?? 0,
                    limit: params?.limit ?? 25,
                    search: params?.search ?? '',
                    organizationKey: params?.organizationKey ?? undefined
                },
                ...hospitalHeaders(params?.hospitalId),
                signal
            });
            return data.items ?? [];
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch users'));
        }
    }
);

export const fetchCensusReportDetails = createAsyncThunk<CensusReportDetailItem, FetchCensusReportDetailsParams, { rejectValue: string }>(
    'census/fetchCensusReportDetails',
    async ({ hospitalId, ...params }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<CensusReportDetailItem>(
                'patients/census-reports-ranges/details',
                params,
                hospitalHeaders(hospitalId)
            );
            return data ?? { ranges: [], coverage: [] };
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch census report details'));
        }
    }
);

/** `POST /patients/census-reports-ranges/coverage-patients` — requires `X-Hospital-Id`. */
export const saveCensusCoveragePatients = createAsyncThunk<
    SaveCensusCoveragePatientsResponse,
    SaveCensusCoveragePatientsParams,
    { rejectValue: string }
>('census/saveCensusCoveragePatients', async ({ items, hospitalId }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.post<SaveCensusCoveragePatientsResponse>(
            'patients/census-reports-ranges/coverage-patients',
            items,
            {
                signal,
                ...hospitalHeaders(hospitalId)
            }
        );

        return {
            items: data?.items ?? []
        };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to save coverage patients'));
    }
});

export const assignCareNavigator = createAsyncThunk<void, AssignCareNavigatorParams, { rejectValue: string }>(
    'census/assignCareNavigator',
    async (params, { rejectWithValue }) => {
        try {
            await axiosInstance.post('patients/hospital-staff/assign-unit-range', params, hospitalHeaders(params.hospitalId));
            return undefined;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to assign care navigator'));
        }
    }
);

export const unassignCareNavigator = createAsyncThunk<void, AssignCareNavigatorParams, { rejectValue: string }>(
    'census/unassignCareNavigator',
    async (params, { rejectWithValue }) => {
        try {
            await axiosInstance.post('patients/hospital-staff/unassign-unit-range', params, hospitalHeaders(params.hospitalId));
            return undefined;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to unassign care navigator'));
        }
    }
);
